import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { KeyRound, LifeBuoy, LogOut, Settings, Inbox, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import InstallButton from "@/components/InstallButton";
import { BrandHomeLink } from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";

const links = [
  { to: "/vault", label: "My Vault", icon: KeyRound, testid: "nav-vault" },
  { to: "/shared", label: "Shared with me", icon: Inbox, testid: "nav-shared" },
  { to: "/emergency", label: "Emergency access", icon: LifeBuoy, testid: "nav-emergency" },
  { to: "/settings#family-invites", label: "Invite family", mobileLabel: "Invite", icon: UserPlus, testid: "nav-invites", ownerOnly: true, inviteLink: true },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isOwner = user?.role === "owner" || user?.role === "admin";
  const visibleLinks = links.filter((link) => !link.ownerOnly || isOwner);

  const linkIsActive = (link, pathIsActive) => {
    const invitesActive = location.pathname === "/settings" && location.hash === "#family-invites";
    if (link.inviteLink) return invitesActive;
    if (link.to === "/settings") return pathIsActive && !invitesActive;
    return pathIsActive;
  };

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--vault-app-bg)" }}>
      {/* Sidebar */}
      <aside
        className="hidden md:flex w-64 flex-col px-5 py-7 shrink-0"
        style={{ background: "var(--vault-sidebar-bg)" }}
        data-testid="sidebar"
      >
        <div className="mb-8 px-1 flex items-center justify-between gap-2">
          <BrandHomeLink size="md" testid="sidebar-brand-logo" linkTestid="sidebar-brand-home-link" />
          <ThemeToggle testid="sidebar-theme-toggle" />
        </div>

        <nav className="space-y-1 flex-1">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `sidebar-link ${linkIsActive(l, isActive) ? "active" : ""}`}
              data-testid={l.testid}
            >
              <l.icon size={18} />
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-6 pt-4 border-t border-[color:var(--vault-border)]">
          <InstallButton variant="outline" className="w-full mb-3" />
          <div className="px-1 pb-3">
            <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
            <div className="text-xs text-[color:var(--vault-text-tertiary)] truncate">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            className="sidebar-link w-full hover:!bg-[color:var(--vault-error-bg)] hover:!text-[color:var(--vault-error)]"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span>Sign out</span>
          </button>
          <p className="mt-4 px-1 text-[11px] leading-snug text-[color:var(--vault-text-tertiary)]" data-testid="sidebar-credit">
            Website Designed and Built by{" "}
            <a href="https://gadgetguymedia.com" target="_blank" rel="noopener noreferrer" className="font-medium text-[color:var(--vault-brand)] hover:underline" data-testid="sidebar-credit-link">Gadget Guy Media</a>
          </p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile topbar */}
        <header className="md:hidden glass-nav px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <BrandHomeLink size="sm" subtitle={null} testid="mobile-brand-logo" linkTestid="mobile-brand-home-link" />
          <div className="flex items-center gap-1">
            <ThemeToggle testid="mobile-theme-toggle" />
            <button onClick={onLogout} className="p-2 rounded-lg hover:bg-[color:var(--vault-brand-light)]" data-testid="mobile-logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* Mobile tabs */}
        <nav className="md:hidden flex justify-around border-b border-[color:var(--vault-border)] bg-white">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[11px] flex-1 min-w-0 ${linkIsActive(l, isActive) ? "text-[color:var(--vault-brand)] border-b-2 border-[color:var(--vault-brand)]" : "text-[color:var(--vault-text-tertiary)]"}`
              }
              data-testid={`mobile-${l.testid}`}
            >
              <l.icon size={16} />
              <span className="truncate max-w-full px-1">{l.mobileLabel || l.label}</span>
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 px-4 md:px-8 lg:px-12 py-6 md:py-10 max-w-[1400px] w-full">
          <Outlet />
        </main>
        <p className="md:hidden text-center text-[11px] text-[color:var(--vault-text-tertiary)] pb-6 px-4" data-testid="mobile-credit">
          Website Designed and Built by{" "}
          <a href="https://gadgetguymedia.com" target="_blank" rel="noopener noreferrer" className="font-medium text-[color:var(--vault-brand)] hover:underline" data-testid="mobile-credit-link">Gadget Guy Media</a>
        </p>
      </div>
    </div>
  );
}
